import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Hls from 'hls.js';
import PhoneShell from '../components/shared/PhoneShell';
import { toggleBetSlip } from '../utils/betSlip';
import { maskStyle } from '../utils/icons';
import { useLang } from '../i18n';

const API = process.env.REACT_APP_API_BASE_URL;

/* ─── Paris timezone utilities ─────────────────────────────────────────── */
const getParisNow = () => {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  const p = fmt.formatToParts(new Date());
  const g = (t) => p.find(x => x.type === t)?.value || '00';
  return { date: `${g('year')}-${g('month')}-${g('day')}`, time: `${g('hour')}:${g('minute')}:${g('second')}` };
};
const timeToSeconds  = (s) => { if (!s) return 0; const [h, m, sec] = s.split(':').map(Number); return h * 3600 + m * 60 + (sec || 0); };
const isTimeAfter    = (d, t) => { const p = getParisNow(); return p.date > d || (p.date === d && timeToSeconds(p.time) >= timeToSeconds(t)); };
const isTimeBefore   = (d, t) => { const p = getParisNow(); return p.date < d || (p.date === d && timeToSeconds(p.time) < timeToSeconds(t)); };
const isTimeBetween  = (d, s, e) => { const p = getParisNow(); if (p.date !== d) return false; const n = timeToSeconds(p.time); return n >= timeToSeconds(s) && n < timeToSeconds(e); };
const getMinutesSince = (d, s) => { const p = getParisNow(); if (p.date !== d) return p.date > d ? 90 : 0; return Math.max(0, Math.floor((timeToSeconds(p.time) - timeToSeconds(s)) / 60)); };
const parseGoalMinute = (t) => parseInt((t || '').replace("'", '').replace('+', '')) || 0;

/* ─── CSS (MatchDetail design) ──────────────────────────────────────────── */
const css = `
  /* layout */
  .lm-wrap { display:flex; flex-direction:column; height:100%; overflow:hidden; }
  .lm-scroll { flex:1; overflow-y:auto; -webkit-overflow-scrolling:touch; padding-bottom:80px; }
  .lm-scroll::-webkit-scrollbar { display:none; }

  /* ── EventHeader ── */
  .eventhead { margin:10px 9px 0; height:144px; border-radius:10px; position:relative; overflow:hidden; flex-shrink:0; }
  .eventhead-bg { position:absolute; inset:0; background-size:cover; background-position:center; }
  .eventhead-bg::after { content:""; position:absolute; inset:0; background:rgba(0,0,0,.5); }
  .eventhead-top { position:relative; z-index:1; padding:6px 8px 0; }
  .eventhead-comp { display:flex; align-items:center; gap:7px; }
  .eventhead-comp .ic { width:12px; height:12px; background:#fff; display:inline-block; flex-shrink:0; }
  .eventhead-comp span { font-size:12px; font-weight:600; color:#fff; }
  .eventhead-date { font-size:10px; color:#ccc; margin:6px 0 0 0; }
  .eventhead-live-btn {
    position:absolute; top:8px; right:8px; z-index:2;
    display:flex; align-items:center; gap:6px; height:26px; padding:0 11px;
    border-radius:13px; background:rgba(0,0,0,.45); box-shadow:inset 0 0 0 1px rgba(118,225,255,.5);
    color:#fff; font-size:11px; font-weight:700; border:none; cursor:pointer;
  }
  .eventhead-live-btn .dot { width:6px; height:6px; border-radius:50%; background:#ff3b3b; flex-shrink:0; animation:eh-pulse 1.2s ease-in-out infinite; }
  @keyframes eh-pulse { 0%,100%{opacity:1} 50%{opacity:.3} }
  .eventhead-teams {
    position:absolute; left:0; right:0; bottom:0; height:89px; z-index:1;
    background:rgba(0,0,0,.5); backdrop-filter:blur(7px);
    padding:10px 13px; display:flex; flex-direction:column; justify-content:center; gap:10px;
  }
  .eventhead-team { display:flex; align-items:center; gap:8px; }
  .eventhead-team img { width:18px; height:18px; object-fit:contain; flex-shrink:0; }
  .eventhead-team-name { font-size:12px; font-weight:600; color:#fff; flex:1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .eventhead-score { font-size:16px; font-weight:800; color:#fff; flex-shrink:0; }
  .eventhead-min { display:inline-flex; align-items:center; gap:4px; font-size:9px; font-weight:800; color:#00d4aa; background:rgba(0,212,170,.15); border:1px solid rgba(0,212,170,.4); border-radius:20px; padding:2px 7px; flex-shrink:0; }
  .eventhead-min-dot { width:5px; height:5px; border-radius:50%; background:#00d4aa; animation:eh-pulse 1.2s ease-in-out infinite; }
  .eventhead-ht { display:inline-flex; align-items:center; font-size:9px; font-weight:800; color:#ffa500; background:rgba(255,165,0,.15); border:1px solid rgba(255,165,0,.4); border-radius:20px; padding:2px 7px; flex-shrink:0; }
  .eventhead-fin { display:inline-flex; align-items:center; font-size:9px; font-weight:800; color:#9b9ab0; background:rgba(90,89,114,.2); border:1px solid rgba(90,89,114,.4); border-radius:20px; padding:2px 7px; flex-shrink:0; }

  /* goals strip */
  .lm-goals-strip { display:flex; gap:8px; padding:8px 9px 0; overflow-x:auto; flex-shrink:0; }
  .lm-goals-strip::-webkit-scrollbar { display:none; }
  .lm-goal-pill { display:flex; align-items:center; gap:5px; padding:3px 9px; border-radius:20px; background:#16142e; border:1px solid rgba(255,255,255,.06); white-space:nowrap; flex-shrink:0; }
  .lm-goal-pill span { font-size:11px; color:#e4e7ec; font-weight:600; }
  .lm-goal-pill .g-min { color:#76e1ff; font-size:10px; font-weight:700; }

  /* video */
  .lm-stream { margin:10px 9px 0; border-radius:10px; overflow:hidden; background:#000; }
  .lm-stream-head { display:flex; align-items:center; gap:8px; padding:9px 12px; background:#16142e; }
  .lm-stream-head span { font-size:12px; font-weight:700; color:#fff; flex:1; }
  .lm-live-tag { font-size:9px; font-weight:800; padding:2px 7px; border-radius:20px; background:rgba(255,59,59,.15); color:#ff3b3b; }
  .lm-ht-tag  { font-size:9px; font-weight:800; padding:2px 7px; border-radius:20px; background:rgba(255,165,0,.15); color:#ffa500; }
  .lm-video-wrap { position:relative; width:100%; aspect-ratio:16/9; }
  .lm-video { width:100%; height:100%; object-fit:contain; display:block; }
  .lm-video-controls { position:absolute; bottom:0; left:0; right:0; padding:7px 10px; background:linear-gradient(transparent,rgba(0,0,0,.75)); display:flex; align-items:center; justify-content:space-between; opacity:0; transition:opacity .3s; }
  .lm-video-controls.show { opacity:1; }
  .lm-ctrl-left { display:flex; align-items:center; gap:8px; }
  .lm-ctrl-right { display:flex; align-items:center; gap:8px; }
  .lm-ctrl-btn { width:30px; height:30px; border-radius:7px; background:rgba(255,255,255,.15); border:none; cursor:pointer; display:grid; place-items:center; color:#fff; }
  .lm-min-tag { font-size:11px; font-weight:700; color:#fff; }
  .lm-play-overlay { position:absolute; inset:0; display:grid; place-items:center; background:rgba(0,0,0,.3); cursor:pointer; }
  .lm-play-circle { width:50px; height:50px; border-radius:50%; background:rgba(255,255,255,.2); display:grid; place-items:center; }
  .lm-unavailable { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:24px 20px; gap:5px; background:#16142e; }
  .lm-unavailable-title { font-size:12px; font-weight:700; color:#fff; }
  .lm-unavailable-text  { font-size:11px; color:#9b9ab0; }

  /* ── MarketFilter ── */
  .marketfilter { display:flex; align-items:center; gap:7px; padding:12px 12px 2px; overflow-x:auto; flex-shrink:0; }
  .marketfilter::-webkit-scrollbar { display:none; }
  .mf-chip { flex:none; height:27px; padding:0 12px; border-radius:5px; background:#1d1c3c; color:#fff; font-size:10px; font-weight:500; display:flex; align-items:center; white-space:nowrap; border:none; cursor:pointer; }
  .mf-chip.is-active { background:linear-gradient(82deg,#8187f6 29%,#76e1ff 190%); }

  /* ── Markets ── */
  .md-markets { padding:0 0 10px; }
  .market-block { padding:14px 9px 0; }
  .market-block-head { display:flex; align-items:center; gap:8px; height:16px; cursor:pointer; user-select:none; }
  .market-block-head .name { font-size:11px; font-weight:500; color:#fff; flex:1; }
  .market-block-head .chev { width:11px; height:11px; background:#757490; transition:transform .2s ease; flex-shrink:0; }
  .market-block.is-collapsed .chev { transform:rotate(180deg); }
  .market-grid { display:grid; gap:6px; margin-top:9px; }
  .market-grid.cols-2 { grid-template-columns:1fr 1fr; }
  .market-grid.cols-3 { grid-template-columns:1fr 1fr 1fr; }
  .market-grid.cols-4 { grid-template-columns:1fr 1fr 1fr 1fr; }
  .market-block.is-collapsed .market-grid { display:none; }

  /* ── OddsButton ── */
  .oddsbtn { display:flex; align-items:center; justify-content:space-between; gap:6px; height:34px; border-radius:5px; background:rgba(29,28,60,.5); padding-left:8px; overflow:hidden; border:none; cursor:pointer; width:100%; }
  .oddsbtn.suspended { opacity:.35; cursor:not-allowed; }
  .oddsbtn .obl { font-size:10px; color:#757490; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; flex:1; text-align:left; }
  .oddsbtn .obv { min-width:46px; height:34px; border-radius:5px; background:#1d1c3c; display:grid; place-items:center; flex:none; }
  .oddsbtn .obv span { font-size:11px; font-weight:500; color:#76e1ff; }
  .oddsbtn.is-selected { box-shadow:inset 0 0 0 1px #76e1ff; background:rgba(118,225,255,.08); }
  .oddsbtn.is-selected .obv { background:linear-gradient(87deg,#8187f6,#76e1ff); }
  .oddsbtn.is-selected .obv span { color:#fff; }
  .oddsbtn:disabled { opacity:.4; cursor:not-allowed; }

  /* ── States ── */
  .md-spinner { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:80px 20px; gap:14px; }
  .md-spinner-ring { width:36px; height:36px; border:3px solid rgba(255,255,255,.06); border-top-color:#00d4aa; border-radius:50%; animation:md-spin 1s linear infinite; }
  @keyframes md-spin { to{transform:rotate(360deg)} }
  .md-spinner p { color:#5a5972; font-size:13px; margin:0; }
  .md-no-odds { padding:20px 14px; }
  .md-no-odds p { color:#3a3857; font-size:12px; margin:0; }

  /* ── Goal overlay ── */
  .lm-goal-overlay { position:fixed; inset:0; z-index:999; display:grid; place-items:center; pointer-events:none; }
  .lm-goal-anim { background:linear-gradient(135deg,#8187f6,#76e1ff); border-radius:16px; padding:20px 32px; text-align:center; animation:lm-goal-pop .4s cubic-bezier(.34,1.56,.64,1) both; }
  @keyframes lm-goal-pop { from{opacity:0;transform:scale(.5)} to{opacity:1;transform:scale(1)} }
  .lm-goal-anim .g-text { display:block; font-size:28px; font-weight:900; color:#fff; }
  .lm-goal-anim .g-team { display:block; font-size:14px; font-weight:700; color:rgba(255,255,255,.85); margin-top:4px; }
  .lm-goal-anim .g-min  { display:block; font-size:12px; color:rgba(255,255,255,.7); margin-top:2px; }
`;

/* ─── Inline SVG icons ──────────────────────────────────────────────────── */
const PlayIcon = ({ size = 18 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg>;
const MuteIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></svg>;
const VolumeIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M15.54 8.46a5 5 0 0 1 0 7.07" /></svg>;
const FullscreenIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></svg>;
const StarIcon = ({ filled }) => <svg width="18" height="18" viewBox="0 0 24 24" fill={filled ? '#ffc107' : 'none'} stroke={filled ? '#ffc107' : '#9b9ab0'} strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>;

/* ─── Main component ────────────────────────────────────────────────────── */
export default function LiveMatch() {
  const { t } = useLang();
  const { matchId } = useParams();
  const navigate = useNavigate();

  const [matchData, setMatchData]     = useState(null);
  const [championship, setChampionship] = useState(null);
  const [currentMinute, setCurrentMinute] = useState(0);
  const [loading, setLoading]         = useState(true);
  const [selBets, setSelBets]         = useState([]);
  const [isFavorite, setIsFavorite]   = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [collapsed, setCollapsed]     = useState({});
  const [lastGoalShown, setLastGoalShown] = useState(null);
  const [showGoalAnim, setShowGoalAnim]   = useState(false);

  const videoRef  = useRef(null);
  const hlsRef    = useRef(null);
  const ctrlTimer = useRef(null);
  const [isPlaying, setIsPlaying]       = useState(false);
  const [isMuted, setIsMuted]           = useState(true);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [showControls, setShowControls] = useState(true);

  /* ── betslip sync ── */
  useEffect(() => {
    const sync = () => setSelBets(JSON.parse(localStorage.getItem('betSlipItems') || '[]').map(b => b.id));
    sync();
    window.addEventListener('betAdded', sync);
    return () => window.removeEventListener('betAdded', sync);
  }, []);

  /* ── favorites ── */
  useEffect(() => {
    const favs = JSON.parse(localStorage.getItem('favoriteMatches') || '[]');
    setIsFavorite(favs.some(f => f.id === parseInt(matchId)));
  }, [matchId]);

  /* ── fetch match ── */
  useEffect(() => {
    const fetchMatch = async () => {
      try {
        const res = await fetch(`${API}/matchs/read.php?match=${matchId}`, {
          headers: { 'ngrok-skip-browser-warning': 'true' },
        });
        if (!res.ok) throw new Error(res.status);
        const data = await res.json();
        let found = null, champ = null;
        if (data?.id) {
          found = data;
          champ = { name: data.league?.name || data.league || data.championship || 'Venezuela D2' };
        } else if (data?.championships) {
          for (const c of data.championships) {
            const m = (c.matches || []).find(x => x.id === parseInt(matchId));
            if (m) { found = m; champ = c; break; }
          }
        }
        if (found) {
          setMatchData(found);
          setChampionship(champ);
          setCurrentMinute(found.status === 'finished' ? 90 : calcMinute(found));
        }
      } catch (e) { console.error('LiveMatch fetch error:', e); }
      finally { setLoading(false); }
    };
    fetchMatch();
  }, [matchId]);

  /* ── minute timer ── */
  useEffect(() => {
    if (!matchData || matchData.status === 'finished' || isFinished(matchData)) return;
    const iv = setInterval(() => {
      if (isFinished(matchData)) { setCurrentMinute(90); clearInterval(iv); return; }
      setCurrentMinute(calcMinute(matchData));
    }, 30000);
    return () => clearInterval(iv);
  }, [matchData]);

  /* ── goal animation ── */
  useEffect(() => {
    if (!matchData?.goals) return;
    const g = matchData.goals.find(x => parseGoalMinute(x.time) === currentMinute);
    if (g && (!lastGoalShown || lastGoalShown.time !== g.time)) {
      setLastGoalShown(g); setShowGoalAnim(true);
      setTimeout(() => setShowGoalAnim(false), 3000);
    }
  }, [currentMinute, matchData, lastGoalShown]);

  /* ── HLS ── */
  useEffect(() => {
    if (!matchData?.streamUrl || !videoRef.current || !streamAvailable(matchData)) return;
    const url = matchData.streamUrl;
    if (Hls.isSupported()) {
      if (hlsRef.current) hlsRef.current.destroy();
      const hls = new Hls({ enableWorker: true, lowLatencyMode: true, backBufferLength: 90, liveSyncDurationCount: 3, liveMaxLatencyDurationCount: 6, liveDurationInfinity: true });
      hls.loadSource(url);
      hls.attachMedia(videoRef.current);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsVideoReady(true);
        videoRef.current.muted = true;
        if (hls.liveSyncPosition) videoRef.current.currentTime = hls.liveSyncPosition;
        videoRef.current.play().then(() => { setIsPlaying(true); setIsMuted(true); }).catch(() => {});
      });
      videoRef.current.addEventListener('loadedmetadata', () => {
        const target = calcVideoTime(matchData);
        if (videoRef.current.duration && target < videoRef.current.duration) videoRef.current.currentTime = target;
      });
      hls.on(Hls.Events.ERROR, (_, d) => {
        if (d.fatal) {
          if (d.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
          else if (d.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
          else hls.destroy();
        }
      });
      hlsRef.current = hls;
      return () => { if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; } };
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      videoRef.current.src = url;
      videoRef.current.addEventListener('loadedmetadata', () => {
        setIsVideoReady(true); videoRef.current.muted = true;
        const target = calcVideoTime(matchData);
        if (videoRef.current.duration && target < videoRef.current.duration) videoRef.current.currentTime = target;
        videoRef.current.play().then(() => { setIsPlaying(true); setIsMuted(true); }).catch(() => {});
      });
    }
  }, [matchData]);

  /* ─── helpers ─────────────────────────────────────────────────────────── */
  const calcMinute = (m) => {
    const kickoff = m?.kickoffTime || m?.livestart;
    if (!kickoff) return 0;
    const d = m.date ? m.date.split('T')[0] : getParisNow().date;
    if (isTimeBefore(d, kickoff)) return 0;
    if (m.endTime && isTimeAfter(d, m.endTime)) return 90;
    if (m.pause && m.restart && isTimeBetween(d, m.pause, m.restart)) return 45;
    if (m.pause && isTimeBetween(d, kickoff, m.pause)) return Math.min(getMinutesSince(d, kickoff), 45);
    if (m.restart && isTimeAfter(d, m.restart)) {
      const min = getMinutesSince(d, m.restart);
      if (m.endTime && !isTimeAfter(d, m.endTime)) return 45 + Math.min(min, 50);
      return 90;
    }
    return Math.min(Math.max(0, getMinutesSince(d, kickoff)), 90);
  };

  /* elapsed seconds since livestart — used to seek a VOD stream to its live position */
  const calcVideoTime = (m) => {
    if (!m?.livestart) return 0;
    const d = m.date ? m.date.split('T')[0] : getParisNow().date;
    const p = getParisNow();
    if (p.date !== d) return 0;
    return Math.max(0, timeToSeconds(p.time) - timeToSeconds(m.livestart));
  };

  const isFinished    = (m) => { if (!m) return false; if (m.status === 'finished') return true; if (m.endTime) return isTimeAfter(m.date ? m.date.split('T')[0] : getParisNow().date, m.endTime); return false; };
  const isHT          = (m) => { if (!m?.pause || !m.restart) return false; return isTimeBetween(m.date ? m.date.split('T')[0] : getParisNow().date, m.pause, m.restart); };
  const streamAvailable = (m) => { if (!m?.livestart || !m.endTime) return false; return isTimeBetween(m.date ? m.date.split('T')[0] : getParisNow().date, m.livestart, m.endTime); };

  const getLiveScore  = () => {
    if (!matchData) return { teamA: 0, teamB: 0 };
    const goals = (matchData.goals || []).filter(g => parseGoalMinute(g.time) <= currentMinute);
    return { teamA: goals.filter(g => g.team === 'A').length, teamB: goals.filter(g => g.team === 'B').length };
  };

  const getDynamicOdds = () => {
    if (!matchData?.odds) return null;
    const base = matchData.odds;
    const score = getLiveScore();
    const diff = score.teamA - score.teamB;
    const progress = Math.min(currentMinute / 90, 1);
    const total = score.teamA + score.teamB;
    const remain = 1 - progress;
    if (currentMinute === 0) return base;
    const vol = 1 + (Math.sin(currentMinute * 0.1) * 0.05);
    return {
      matchResult: {
        teamA: Math.max(1.01, base.matchResult.teamA * (1 - diff * 0.3 + progress * 0.2) * vol),
        draw:  Math.max(1.01, base.matchResult.draw  * (1 - Math.abs(diff) * 0.35 + progress * 0.15) * vol),
        teamB: Math.max(1.01, base.matchResult.teamB * (1 + diff * 0.3 + progress * 0.2) * vol),
      },
      doubleChance: {
        teamAOrDraw:  Math.max(1.01, base.doubleChance.teamAOrDraw  * (1 - diff * 0.2 * remain)),
        teamAOrTeamB: Math.max(1.01, base.doubleChance.teamAOrTeamB * (1 + progress * 0.08)),
        drawOrTeamB:  Math.max(1.01, base.doubleChance.drawOrTeamB  * (1 + diff * 0.2 * remain)),
      },
      bothTeamsScore: {
        yes: Math.max(1.01, base.bothTeamsScore.yes * (score.teamA > 0 && score.teamB > 0 ? 0.3 : total > 0 ? 0.7 * (1 - progress * 0.3) : 1.1 + progress * 0.4)),
        no:  Math.max(1.01, base.bothTeamsScore.no  * (score.teamA > 0 && score.teamB > 0 ? 3.0 : total > 0 ? 1.4 : 0.85 - progress * 0.2)),
      },
      totalGoals: {
        over15:  Math.max(1.01, base.totalGoals.over15  * (total >= 2 ? 0.25 : total >= 1 ? 0.55 : 1.15 + progress * 0.3)),
        under15: Math.max(1.01, base.totalGoals.under15 * (total >= 2 ? 3.5  : total >= 1 ? 2.0  : 0.85 - progress * 0.3)),
        over25:  Math.max(1.01, base.totalGoals.over25  * (total >= 3 ? 0.35 : total >= 2 ? 0.65 : total >= 1 ? 0.9  : 1.1 + progress * 0.4)),
        under25: Math.max(1.01, base.totalGoals.under25 * (total >= 3 ? 2.8  : total >= 2 ? 1.7  : total >= 1 ? 1.1  : 0.9 - progress * 0.25)),
        over35:  Math.max(1.01, base.totalGoals.over35  * (total >= 4 ? 0.45 : total >= 3 ? 0.75 : total >= 2 ? 0.95 : 1.05 + progress * 0.5)),
        under35: Math.max(1.01, base.totalGoals.under35 * (total >= 4 ? 2.2  : total >= 3 ? 1.5  : total >= 2 ? 1.15 : 0.95 - progress * 0.2)),
      },
      exactScore: Object.fromEntries(Object.entries(base.exactScore).map(([sc, odd]) => {
        const [a, b] = sc.split('-').map(Number);
        const isCur = a === score.teamA && b === score.teamB;
        const ok    = a >= score.teamA && b >= score.teamB;
        if (!ok)  return [sc, 50];
        if (isCur) return [sc, Math.max(1.01, odd * (0.3 + progress * 0.6))];
        return [sc, Math.max(1.01, odd * (1.2 + progress * 0.5))];
      })),
      goalDifference: {
        teamABy1:    Math.max(1.01, base.goalDifference.teamABy1    * (diff === 1  ? 0.5 - progress * 0.3 : diff > 1  ? 4.0 : 1.1)),
        teamABy2:    Math.max(1.01, base.goalDifference.teamABy2    * (diff === 2  ? 0.5 - progress * 0.3 : diff > 2  ? 4.0 : 1.15)),
        teamABy3Plus:Math.max(1.01, base.goalDifference.teamABy3Plus* (diff >= 3   ? 0.4 - progress * 0.25 : 1.2)),
        teamBBy1:    Math.max(1.01, base.goalDifference.teamBBy1    * (diff === -1 ? 0.5 - progress * 0.3 : diff < -1 ? 4.0 : 1.1)),
        teamBBy2:    Math.max(1.01, base.goalDifference.teamBBy2    * (diff === -2 ? 0.5 - progress * 0.3 : diff < -2 ? 4.0 : 1.15)),
        teamBBy3Plus:Math.max(1.01, base.goalDifference.teamBBy3Plus* (diff <= -3  ? 0.4 - progress * 0.25 : 1.2)),
      },
      halfTime: {
        teamA: currentMinute >= 45 ? 50 : Math.max(1.01, base.halfTime.teamA * (1 - diff * 0.25)),
        draw:  currentMinute >= 45 ? 50 : Math.max(1.01, base.halfTime.draw  * (1 - Math.abs(diff) * 0.3)),
        teamB: currentMinute >= 45 ? 50 : Math.max(1.01, base.halfTime.teamB * (1 + diff * 0.25)),
      },
      ...(base.firstGoalScorer   ? { firstGoalScorer:   { ...base.firstGoalScorer   } } : {}),
      ...(base.halfTimeFullTime  ? { halfTimeFullTime:  { ...base.halfTimeFullTime  } } : {}),
    };
  };

  /* ─── video controls ─────────────────────────────────────────────────── */
  /* direct = non stoppable : toute mise en pause relance et recale sur le live */
  const forceLive = () => {
    if (!videoRef.current) return;
    const target = calcVideoTime(matchData);
    if (videoRef.current.duration && target < videoRef.current.duration) videoRef.current.currentTime = target;
    videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
  };
  const toggleMute  = () => { if (!videoRef.current) return; videoRef.current.muted = !isMuted; setIsMuted(!isMuted); };
  const handleFullscreen = () => { const el = videoRef.current?.parentElement; if (!el) return; document.fullscreenElement ? document.exitFullscreen() : el.requestFullscreen?.(); };
  const onVideoTouch = () => { setShowControls(true); clearTimeout(ctrlTimer.current); ctrlTimer.current = setTimeout(() => { if (isPlaying) setShowControls(false); }, 3000); };

  /* ─── addBet ─────────────────────────────────────────────────────────── */
  const addBet = (selection, odds, betType) => {
    if (isFinished(matchData)) return;
    toggleBetSlip({
      id: `live-${matchData.id}-${betType}`,
      matchId: matchData.id,
      match: `${matchData.teamA?.name} vs ${matchData.teamB?.name}`,
      date: matchData.date ? new Date(matchData.date).toLocaleDateString('fr-FR') : '',
      selection,
      odds: parseFloat(odds),
      stake: 0,
      league: championship?.name || '',
      sport: 'football',
    });
  };

  /* ─── favorite ───────────────────────────────────────────────────────── */
  const toggleFavorite = () => {
    const favs = JSON.parse(localStorage.getItem('favoriteMatches') || '[]');
    if (isFavorite) { localStorage.setItem('favoriteMatches', JSON.stringify(favs.filter(f => f.id !== parseInt(matchId)))); setIsFavorite(false); }
    else { localStorage.setItem('favoriteMatches', JSON.stringify([...favs, { id: parseInt(matchId), type: 'live', teamA: matchData.teamA?.name, teamB: matchData.teamB?.name, championship: championship?.name, date: matchData.date, status: matchData.status, addedAt: new Date().toISOString() }])); setIsFavorite(true); }
  };

  /* ─── sections config ────────────────────────────────────────────────── */
  const SECTIONS = [
    { key: 'main',           label: t('liveMatch.market.1x2') },
    { key: 'doubleChance',   label: t('liveMatch.market.doubleChance') },
    { key: 'totalGoals',     label: t('liveMatch.market.overUnder') },
    { key: 'btts',           label: t('liveMatch.market.btts') },
    { key: 'halftime',       label: t('liveMatch.market.halftime') },
    { key: 'exactScore',     label: t('liveMatch.market.exactScore') },
    { key: 'goalDifference', label: t('liveMatch.market.goalDiff') },
  ];

  const sectionVisible = (key) => activeFilter === 'all' || activeFilter === key;
  const toggle = (k) => setCollapsed(p => ({ ...p, [k]: !p[k] }));

  /* ─── render ─────────────────────────────────────────────────────────── */
  if (loading) return (
    <PhoneShell activeTab="sports">
      <style>{css}</style>
      <div className="md-spinner"><div className="md-spinner-ring" /><p>{t('sports.loading')}</p></div>
    </PhoneShell>
  );

  if (!matchData) return (
    <PhoneShell activeTab="sports">
      <style>{css}</style>
      <div className="md-spinner"><p style={{ color: '#3a3857' }}>{t('sports.matchNotFound')}</p></div>
    </PhoneShell>
  );

  const score  = getLiveScore();
  const odds   = getDynamicOdds();
  const status = (() => {
    if (matchData.status === 'finished' || isFinished(matchData)) return 'finished';
    if (isHT(matchData)) return 'halftime';
    if (currentMinute > 45) return 'second';
    if (currentMinute > 0)  return 'first';
    return 'scheduled';
  })();
  const betting    = status === 'finished';
  const visGoals   = (matchData.goals || []).filter(g => parseGoalMinute(g.time) <= currentMinute);
  const dateLabel  = matchData.date ? `${matchData.date.split('T')[0].slice(5).split('-').reverse().join('/')} ${matchData.livestart?.slice(0,5) || ''}`.trim() : matchData.livestart?.slice(0,5) || '';

  if (!odds) return (
    <PhoneShell activeTab="sports">
      <style>{css}</style>
      <div className="md-spinner"><p style={{ color: '#3a3857' }}>{t('liveMatch.oddsUnavailable')}</p></div>
    </PhoneShell>
  );

  /* label = texte affiché, sel = chaîne de sélection structurée (identique FDJ) */
  const OddBtn = ({ betType, label, sel, value, selId }) => (
    <button
      className={'oddsbtn' + (selBets.includes(selId) ? ' is-selected' : '')}
      onClick={() => addBet(sel || label, value, betType)}
      disabled={betting}
    >
      <span className="obl">{label}</span>
      <span className="obv"><span>{betting ? '-' : value}</span></span>
    </button>
  );

  return (
    <PhoneShell activeTab="sports">
      <style>{css}</style>

      {/* goal overlay */}
      {showGoalAnim && lastGoalShown && (
        <div className="lm-goal-overlay">
          <div className="lm-goal-anim">
            <span className="g-text">BUT !</span>
            <span className="g-team">{lastGoalShown.team === 'A' ? matchData.teamA?.name : matchData.teamB?.name}</span>
            <span className="g-min">{lastGoalShown.time}</span>
          </div>
        </div>
      )}

      <div className="lm-wrap">
        <div className="lm-scroll">

          {/* ── EventHeader ── */}
          <section className="eventhead">
            <div className="eventhead-bg" style={{ backgroundImage: 'url(/img/event-banner.png)' }} />

            {/* live / status badge */}
            {status === 'finished' ? (
              <span className="eventhead-live-btn" style={{ cursor: 'default' }}><span className="eventhead-fin">FIN</span></span>
            ) : status === 'halftime' ? (
              <span className="eventhead-live-btn" style={{ cursor: 'default' }}><span className="eventhead-ht">MI-TEMPS</span></span>
            ) : (status === 'first' || status === 'second') ? (
              <button className="eventhead-live-btn" onClick={toggleFavorite}>
                <span className="dot" />{currentMinute > 90 ? '90+' : currentMinute}'
              </button>
            ) : (
              <button className="eventhead-live-btn" onClick={toggleFavorite}>
                <StarIcon filled={isFavorite} />
              </button>
            )}

            <div className="eventhead-top">
              <div className="eventhead-comp">
                <span className="ic" style={maskStyle('ic-star')} />
                <span>{championship?.name || 'Venezuela D2'}</span>
              </div>
              <p className="eventhead-date">{dateLabel}</p>
            </div>

            <div className="eventhead-teams">
              <div className="eventhead-team">
                {matchData.teamA?.logo && <img src={matchData.teamA.logo} alt="" />}
                <span className="eventhead-team-name">{matchData.teamA?.name}</span>
                {status !== 'scheduled'
                  ? <span className="eventhead-score">{score.teamA}</span>
                  : (status === 'first' || status === 'second') && <span className="eventhead-min"><span className="eventhead-min-dot" />{currentMinute}'</span>
                }
              </div>
              <div className="eventhead-team">
                {matchData.teamB?.logo && <img src={matchData.teamB.logo} alt="" />}
                <span className="eventhead-team-name">{matchData.teamB?.name}</span>
                {status !== 'scheduled' && <span className="eventhead-score">{score.teamB}</span>}
              </div>
            </div>
          </section>

          {/* goals strip */}
          {visGoals.length > 0 && (
            <div className="lm-goals-strip">
              {visGoals.map((g, i) => (
                <div className="lm-goal-pill" key={i}>
                  <span>⚽</span>
                  <span>{g.team === 'A' ? matchData.teamA?.name : matchData.teamB?.name}</span>
                  <span className="g-min">{g.time}</span>
                </div>
              ))}
            </div>
          )}

          {/* ── Video stream ── */}
          {matchData.streamUrl && (
            <div className="lm-stream">
              <div className="lm-stream-head">
                <PlayIcon size={14} />
                <span>{t('liveMatch.broadcastLive')}</span>
                {streamAvailable(matchData) && !isHT(matchData) && <span className="lm-live-tag">EN DIRECT</span>}
                {isHT(matchData) && <span className="lm-ht-tag">MI-TEMPS</span>}
              </div>
              {streamAvailable(matchData) ? (
                <div className="lm-video-wrap" onMouseMove={onVideoTouch} onTouchStart={onVideoTouch} onMouseLeave={() => { if (isPlaying) setShowControls(false); }}>
                  <video ref={videoRef} className="lm-video" onPlay={() => setIsPlaying(true)} onPause={forceLive} playsInline muted />
                  <div className={'lm-video-controls' + (showControls ? ' show' : '')}>
                    <div className="lm-ctrl-left">
                      <button className="lm-ctrl-btn" onClick={toggleMute}>{isMuted ? <MuteIcon /> : <VolumeIcon />}</button>
                      {isHT(matchData) ? <span className="lm-ht-tag">MI-TEMPS</span> : <span className="lm-live-tag">EN DIRECT</span>}
                    </div>
                    <div className="lm-ctrl-right">
                      <span className="lm-min-tag">{currentMinute > 90 ? '90+' : currentMinute}'</span>
                      <button className="lm-ctrl-btn" onClick={handleFullscreen}><FullscreenIcon /></button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="lm-unavailable">
                  <PlayIcon size={32} />
                  {matchData.endTime && isTimeAfter(matchData.date || getParisNow().date, matchData.endTime)
                    ? <><span className="lm-unavailable-title">{t('liveMatch.broadcastEnded')}</span><span className="lm-unavailable-text">{t('liveMatch.matchOver')}</span></>
                    : <><span className="lm-unavailable-title">{t('liveMatch.broadcastComing')}</span><span className="lm-unavailable-text">{t('liveMatch.startsAt', { time: matchData.livestart?.slice(0,5) })}</span></>
                  }
                </div>
              )}
            </div>
          )}

          {/* ── MarketFilter chips ── */}
          <div className="marketfilter">
            {[{ key: 'all', label: t('liveMatch.allMarkets') }, ...SECTIONS].map(({ key, label }) => (
              <button key={key} className={'mf-chip' + (activeFilter === key ? ' is-active' : '')} onClick={() => setActiveFilter(key)}>
                {label}
              </button>
            ))}
          </div>

          {/* ── Markets ── */}
          {betting ? (
            <div className="md-no-odds"><p>{t('liveMatch.matchFinishedNoBets')}</p></div>
          ) : (
            <div className="md-markets">

              {sectionVisible('main') && (
                <section className={'market-block' + (collapsed.main ? ' is-collapsed' : '')}>
                  <div className="market-block-head" onClick={() => toggle('main')}>
                    <span className="name">1X2</span>
                    <span className="chev" style={maskStyle('ic-chevron')} />
                  </div>
                  <div className="market-grid cols-3">
                    <OddBtn betType="match-1" label={matchData.teamA?.name} sel="Match Winner: Home"  value={odds.matchResult.teamA.toFixed(2)} selId={`live-${matchData.id}-match-1`} />
                    <OddBtn betType="match-x" label={t('liveMatch.draw')}                   sel="Match Winner: Draw"  value={odds.matchResult.draw.toFixed(2)}  selId={`live-${matchData.id}-match-x`} />
                    <OddBtn betType="match-2" label={matchData.teamB?.name} sel="Match Winner: Away"  value={odds.matchResult.teamB.toFixed(2)} selId={`live-${matchData.id}-match-2`} />
                  </div>
                </section>
              )}

              {sectionVisible('doubleChance') && (
                <section className={'market-block' + (collapsed.doubleChance ? ' is-collapsed' : '')}>
                  <div className="market-block-head" onClick={() => toggle('doubleChance')}>
                    <span className="name">{t('liveMatch.market.doubleChance')}</span>
                    <span className="chev" style={maskStyle('ic-chevron')} />
                  </div>
                  <div className="market-grid cols-3">
                    <OddBtn betType="dc-1x" label="1X" sel="Double Chance: 1X" value={odds.doubleChance.teamAOrDraw.toFixed(2)}  selId={`live-${matchData.id}-dc-1x`} />
                    <OddBtn betType="dc-12" label="12" sel="Double Chance: 12" value={odds.doubleChance.teamAOrTeamB.toFixed(2)} selId={`live-${matchData.id}-dc-12`} />
                    <OddBtn betType="dc-x2" label="X2" sel="Double Chance: X2" value={odds.doubleChance.drawOrTeamB.toFixed(2)}  selId={`live-${matchData.id}-dc-x2`} />
                  </div>
                </section>
              )}

              {sectionVisible('totalGoals') && (
                <section className={'market-block' + (collapsed.totalGoals ? ' is-collapsed' : '')}>
                  <div className="market-block-head" onClick={() => toggle('totalGoals')}>
                    <span className="name">{t('liveMatch.market.overUnder')}</span>
                    <span className="chev" style={maskStyle('ic-chevron')} />
                  </div>
                  <div className="market-grid cols-2">
                    {[
                      ['o15','Plus de 1.5', 'Goals Over/Under: Over (1.5)',  odds.totalGoals.over15],
                      ['u15','Moins de 1.5','Goals Over/Under: Under (1.5)', odds.totalGoals.under15],
                      ['o25','Plus de 2.5', 'Goals Over/Under: Over (2.5)',  odds.totalGoals.over25],
                      ['u25','Moins de 2.5','Goals Over/Under: Under (2.5)', odds.totalGoals.under25],
                      ['o35','Plus de 3.5', 'Goals Over/Under: Over (3.5)',  odds.totalGoals.over35],
                      ['u35','Moins de 3.5','Goals Over/Under: Under (3.5)', odds.totalGoals.under35],
                    ].map(([bt, lbl, sel, val]) => <OddBtn key={bt} betType={bt} label={lbl} sel={sel} value={val.toFixed(2)} selId={`live-${matchData.id}-${bt}`} />)}
                  </div>
                </section>
              )}

              {sectionVisible('btts') && (
                <section className={'market-block' + (collapsed.btts ? ' is-collapsed' : '')}>
                  <div className="market-block-head" onClick={() => toggle('btts')}>
                    <span className="name">{t('liveMatch.market.btts')}</span>
                    <span className="chev" style={maskStyle('ic-chevron')} />
                  </div>
                  <div className="market-grid cols-2">
                    <OddBtn betType="btts-yes" label={t('common.yes')} sel="Both Teams Score: Yes" value={odds.bothTeamsScore.yes.toFixed(2)} selId={`live-${matchData.id}-btts-yes`} />
                    <OddBtn betType="btts-no"  label={t('common.no')}  sel="Both Teams Score: No"  value={odds.bothTeamsScore.no.toFixed(2)}  selId={`live-${matchData.id}-btts-no`} />
                  </div>
                </section>
              )}

              {sectionVisible('halftime') && currentMinute < 45 && (
                <section className={'market-block' + (collapsed.halftime ? ' is-collapsed' : '')}>
                  <div className="market-block-head" onClick={() => toggle('halftime')}>
                    <span className="name">{t('liveMatch.market.halftime')}</span>
                    <span className="chev" style={maskStyle('ic-chevron')} />
                  </div>
                  <div className="market-grid cols-3">
                    <OddBtn betType="ht-1" label={matchData.teamA?.name} sel="Halftime Result: Home" value={odds.halfTime.teamA.toFixed(2)} selId={`live-${matchData.id}-ht-1`} />
                    <OddBtn betType="ht-x" label={t('liveMatch.draw')}                   sel="Halftime Result: Draw" value={odds.halfTime.draw.toFixed(2)}  selId={`live-${matchData.id}-ht-x`} />
                    <OddBtn betType="ht-2" label={matchData.teamB?.name} sel="Halftime Result: Away" value={odds.halfTime.teamB.toFixed(2)} selId={`live-${matchData.id}-ht-2`} />
                  </div>
                </section>
              )}

              {sectionVisible('exactScore') && (
                <section className={'market-block' + (collapsed.exactScore ? ' is-collapsed' : '')}>
                  <div className="market-block-head" onClick={() => toggle('exactScore')}>
                    <span className="name">{t('liveMatch.market.exactScore')}</span>
                    <span className="chev" style={maskStyle('ic-chevron')} />
                  </div>
                  <div className="market-grid cols-4">
                    {Object.entries(odds.exactScore).filter(([, o]) => o < 500).map(([sc, o]) => (
                      <OddBtn key={sc} betType={`exact-${sc}`} label={sc} sel={`Exact Score: ${sc}`} value={o.toFixed(2)} selId={`live-${matchData.id}-exact-${sc}`} />
                    ))}
                  </div>
                </section>
              )}

              {sectionVisible('goalDifference') && (
                <section className={'market-block' + (collapsed.goalDifference ? ' is-collapsed' : '')}>
                  <div className="market-block-head" onClick={() => toggle('goalDifference')}>
                    <span className="name">{t('liveMatch.market.goalDiff')}</span>
                    <span className="chev" style={maskStyle('ic-chevron')} />
                  </div>
                  <div className="market-grid cols-2">
                    {[
                      ['gd-a1',`${matchData.teamA?.name} par 1`, 'Winning Margin: Home by 1',  odds.goalDifference.teamABy1],
                      ['gd-a2',`${matchData.teamA?.name} par 2`, 'Winning Margin: Home by 2',  odds.goalDifference.teamABy2],
                      ['gd-a3',`${matchData.teamA?.name} par 3+`,'Winning Margin: Home by 3+', odds.goalDifference.teamABy3Plus],
                      ['gd-b1',`${matchData.teamB?.name} par 1`, 'Winning Margin: Away by 1',  odds.goalDifference.teamBBy1],
                      ['gd-b2',`${matchData.teamB?.name} par 2`, 'Winning Margin: Away by 2',  odds.goalDifference.teamBBy2],
                      ['gd-b3',`${matchData.teamB?.name} par 3+`,'Winning Margin: Away by 3+', odds.goalDifference.teamBBy3Plus],
                    ].map(([bt, lbl, sel, val]) => <OddBtn key={bt} betType={bt} label={lbl} sel={sel} value={val.toFixed(2)} selId={`live-${matchData.id}-${bt}`} />)}
                  </div>
                </section>
              )}

            </div>
          )}
        </div>
      </div>
    </PhoneShell>
  );
}
